// The rule editor: one schedule rule's typed reading of the contract's
// sentence (kind, when it is owed, its first date, its cadence), with the
// sentence itself beside it. Used for award rules (the deliverable page) and
// for a SOW's team rules (the assignment drawer, gate "sow").
//
// A rule with no first date is allowed: it is what the contract says. It
// mints nothing and reads "needs a start date" until someone states one.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraDateField, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraSegmentedControl, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { useCreateRule, useDeleteRule, useLogContractEvent, useUpdateRule } from '../hooks';
import { cmDate } from '../lib/format';
import { CADENCES, describeRule, EVENT_KINDS, mintReceipt, OFFSET_UNITS, ruleLacksStart } from '../lib/rule';
import { useCmLookups } from '../lookups';
import { CmDrawer } from './CmDrawer';
export function draftFromRule(r, teamRule = false) {
    return {
        kind: r?.kind || 'recurring',
        gate: teamRule ? 'sow' : r?.gate || 'optional_task',
        periodOfPerformanceId: r?.periodOfPerformanceId ?? '',
        optionalTaskId: r?.optionalTaskId ?? '',
        start: r?.dueOn ? 'date' : r?.hasOffset ? 'offset' : r ? 'none' : 'offset',
        dueOn: r?.dueOn ?? '',
        offsetN: r?.hasOffset ? String(r.offsetN) : '',
        offsetUnit: r?.offsetUnit || 'calendar_day',
        cadence: r?.cadence || 'monthly',
        eventKind: r?.eventKind ?? '',
        ruleText: r?.ruleText ?? '',
        sourceLocator: r?.sourceLocator ?? '',
        base: r
    };
}
/** The typed fields ScheduleRuleFields wants; the offset keeps its sign. */ export function fieldsFromDraft(d) {
    const isEvent = d.kind === 'event';
    const useOffset = isEvent || d.start === 'offset';
    const n = Number.parseInt(d.offsetN, 10);
    const hasOffset = useOffset && Number.isFinite(n);
    const b = d.base;
    // A day rule and an every-N interval belong to the cadence they were
    // stated for; they are kept only while the cadence and kind are unchanged.
    const sameCadence = !!b && b.kind === d.kind && b.cadence === d.cadence && d.kind === 'recurring';
    return {
        kind: d.kind,
        gate: d.gate,
        periodOfPerformanceId: [
            'base',
            'option_period'
        ].includes(d.gate) ? d.periodOfPerformanceId : '',
        optionalTaskId: d.gate === 'optional_task' ? d.optionalTaskId : '',
        dueOn: !isEvent && d.start === 'date' ? d.dueOn : '',
        hasOffset,
        offsetN: hasOffset ? n : 0,
        offsetUnit: hasOffset ? d.offsetUnit : '',
        cadence: d.kind === 'recurring' ? d.cadence : '',
        cadenceInterval: sameCadence ? b.cadenceInterval : 0,
        dayOrdinals: sameCadence ? [
            ...b.dayOrdinals
        ] : [],
        dayWeekday: sameCadence ? b.dayWeekday : '',
        dayBasis: sameCadence ? b.dayBasis : '',
        eventKind: isEvent ? d.eventKind : '',
        subjects: b ? [
            ...b.subjects
        ] : [],
        ruleText: d.ruleText.trim(),
        sourceLocator: d.sourceLocator.trim(),
        clinId: b?.clinId ?? '',
        sourceDocumentId: b?.sourceDocumentId ?? '',
        effectiveFrom: b?.effectiveFrom ?? '',
        effectiveTo: b?.effectiveTo ?? '',
        establishedByModificationId: b?.establishedByModificationId ?? ''
    };
}
/** What blocks a save (the rest the server's CHECKs name). */ export function draftProblem(d) {
    if ([
        'base',
        'option_period'
    ].includes(d.gate) && !d.periodOfPerformanceId) return 'Pick the period this is owed in.';
    if (d.gate === 'optional_task' && !d.optionalTaskId) return 'Pick the optional task.';
    if (d.kind === 'event' && !d.eventKind) return 'Pick the event.';
    if (d.kind === 'event' && d.offsetN.trim() === '') return 'Say how long after the event.';
    if (d.kind !== 'event' && d.start === 'date' && !d.dueOn) return 'Enter the stated date.';
    if (d.kind !== 'event' && d.start === 'offset' && d.offsetN.trim() === '') return 'Say how long after the start.';
    // pangaea keeps the verbatim rule on every rule (rule_text is required).
    if (!d.ruleText.trim()) return 'Paste the sentence the rule comes from.';
    return null;
}
function draftAsRule(d) {
    const f = fieldsFromDraft(d);
    return {
        gate: f.gate ?? '',
        kind: f.kind ?? '',
        dueOn: f.dueOn ?? '',
        hasOffset: f.hasOffset ?? false,
        offsetN: f.offsetN ?? 0,
        offsetUnit: f.offsetUnit ?? '',
        cadence: f.cadence ?? '',
        cadenceInterval: f.cadenceInterval ?? 0,
        dayOrdinals: f.dayOrdinals ?? [],
        dayWeekday: f.dayWeekday ?? '',
        dayBasis: f.dayBasis ?? '',
        eventKind: f.eventKind ?? '',
        effectiveFrom: f.effectiveFrom ?? '',
        effectiveTo: f.effectiveTo ?? ''
    };
}
/** The controlled form. `teamRule` pins the gate to the SOW. */ export function RuleForm({ programId, value, onChange, teamRule = false }) {
    const lk = useCmLookups(programId);
    const set = (k, v)=>onChange({
            ...value,
            [k]: v
        });
    const isEvent = value.kind === 'event';
    const reading = describeRule(draftAsRule(value));
    const lacks = !isEvent && ruleLacksStart(draftAsRule(value));
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        "data-testid": Testids.cmDuty.ruleEditor,
        children: [
            /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "How often",
                value: value.kind,
                options: [
                    {
                        value: 'recurring',
                        label: 'Recurring'
                    },
                    {
                        value: 'dated',
                        label: 'Once'
                    },
                    {
                        value: 'event',
                        label: 'After an event'
                    }
                ],
                onValueChange: (k)=>set('kind', k),
                testId: Testids.cmDuty.ruleKind
            }),
            teamRule ? null : /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Owed during",
                        required: true,
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: value.gate,
                            onChange: (e)=>set('gate', e.target.value),
                            "data-testid": Testids.cmDuty.ruleGate,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "optional_task",
                                    children: "An optional task’s exercises"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "base",
                                    children: "The base period"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "option_period",
                                    children: "An option period"
                                }),
                                isEvent ? /*#__PURE__*/ _jsx("option", {
                                    value: "none",
                                    children: "Whenever the event happens"
                                }) : null
                            ]
                        })
                    }),
                    value.gate === 'optional_task' ? /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Optional task",
                        required: true,
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: value.optionalTaskId,
                            onChange: (e)=>set('optionalTaskId', e.target.value),
                            "data-testid": Testids.cmDuty.ruleGateTarget,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Pick…"
                                }),
                                lk.optionalTasks.map((t)=>/*#__PURE__*/ _jsxs("option", {
                                        value: t.id,
                                        children: [
                                            t.short,
                                            t.name && t.name !== t.short ? ` · ${t.name}` : ''
                                        ]
                                    }, t.id))
                            ]
                        })
                    }) : [
                        'base',
                        'option_period'
                    ].includes(value.gate) ? /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Period",
                        required: true,
                        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                            value: value.periodOfPerformanceId,
                            onChange: (e)=>set('periodOfPerformanceId', e.target.value),
                            "data-testid": Testids.cmDuty.ruleGateTarget,
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "",
                                    children: "Pick…"
                                }),
                                lk.periods.map((p)=>/*#__PURE__*/ _jsx("option", {
                                        value: p.id,
                                        children: p.name
                                    }, p.id))
                            ]
                        })
                    }) : null
                ]
            }),
            isEvent ? /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "After",
                required: true,
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    value: value.eventKind,
                    onChange: (e)=>set('eventKind', e.target.value),
                    "data-testid": Testids.cmDuty.ruleEventKind,
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Pick the event…"
                        }),
                        EVENT_KINDS.map((k)=>/*#__PURE__*/ _jsx("option", {
                                value: k.value,
                                children: k.label
                            }, k.value))
                    ]
                })
            }) : /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "First date",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    value: value.start,
                    onChange: (e)=>set('start', e.target.value),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "offset",
                            children: "Some time after the start"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "date",
                            children: "On a stated date"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "none",
                            children: "Not stated in the contract"
                        })
                    ]
                })
            }),
            !isEvent && value.start === 'date' ? /*#__PURE__*/ _jsx(SpecraDateField, {
                picker: "native",
                label: "Stated date",
                required: true,
                value: value.dueOn,
                onValueChange: (v)=>set('dueOn', v),
                "data-testid": Testids.cmDuty.ruleDueOn
            }) : null,
            isEvent || value.start === 'offset' ? /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: isEvent ? 'How long after' : 'How long after the start',
                        required: true,
                        hint: "A negative number means before, e.g. −5.",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            type: "number",
                            value: value.offsetN,
                            onChange: (e)=>set('offsetN', e.target.value),
                            "data-testid": Testids.cmDuty.ruleOffsetN
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Unit",
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            value: value.offsetUnit,
                            onChange: (e)=>set('offsetUnit', e.target.value),
                            "data-testid": Testids.cmDuty.ruleOffsetUnit,
                            children: OFFSET_UNITS.map((u)=>/*#__PURE__*/ _jsx("option", {
                                    value: u.value,
                                    children: u.label
                                }, u.value))
                        })
                    })
                ]
            }) : null,
            value.kind === 'recurring' ? /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Then",
                children: /*#__PURE__*/ _jsx(SpecraSelect, {
                    value: value.cadence,
                    onChange: (e)=>set('cadence', e.target.value),
                    "data-testid": Testids.cmDuty.ruleCadence,
                    children: CADENCES.map((c)=>/*#__PURE__*/ _jsx("option", {
                            value: c.value,
                            children: c.label
                        }, c.value))
                })
            }) : null,
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "As the contract states it",
                required: true,
                hint: "Paste the sentence, e.g. “Due seven days after Optional Task Exercise and monthly thereafter.”",
                children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                    rows: 2,
                    value: value.ruleText,
                    onChange: (e)=>set('ruleText', e.target.value),
                    "data-testid": Testids.cmDuty.ruleTextInput
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Where it says so",
                hint: "e.g. “Award PWS 5.4.7.1, p.66”",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    value: value.sourceLocator,
                    onChange: (e)=>set('sourceLocator', e.target.value)
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                "data-testid": Testids.cmDuty.ruleReading,
                children: [
                    "→ ",
                    reading
                ]
            }),
            lacks ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "warning",
                variant: "framed",
                "data-testid": Testids.cmDuty.needsStart,
                children: "Needs a start date: this rule has no first date, so no dates exist until one is stated."
            }) : null
        ]
    });
}
/** The award-rule drawer on the deliverable page: create, edit or delete. */ export function RuleEditorDrawer({ programId, dutyId, rule, onClose, onSaved }) {
    const [draft, setDraft] = useState(()=>draftFromRule(rule));
    const create = useCreateRule(programId, {
        errors: 'inline'
    });
    const update = useUpdateRule(programId, {
        errors: 'inline'
    });
    const del = useDeleteRule(programId, {
        errors: 'inline'
    });
    const busy = create.isPending || update.isPending || del.isPending;
    const problem = draftProblem(draft);
    const error = create.error ?? update.error ?? del.error;
    const save = ()=>{
        const fields = fieldsFromDraft(draft);
        const done = {
            onSuccess: (r)=>onSaved(mintReceipt(r.result?.mint))
        };
        if (rule) update.mutate({
            ruleId: rule.id,
            fields
        }, done);
        else create.mutate({
            dutyId,
            fields
        }, done);
    };
    return /*#__PURE__*/ _jsxs(CmDrawer, {
        title: rule ? 'Edit rule' : 'Add the rule the award states',
        description: "Each date comes from a rule. Saving re-schedules this deliverable's dates.",
        onClose: onClose,
        busy: busy,
        testId: Testids.cmDuty.ruleEditorDrawer,
        footerProps: {
            confirmLabel: 'Save rule',
            onConfirm: save,
            confirmDisabled: !!problem || busy,
            loading: busy,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmDuty.ruleSave
            },
            leading: rule ? /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "destructive",
                disabled: busy,
                onClick: ()=>del.mutate({
                        ruleId: rule.id
                    }, {
                        onSuccess: (r)=>onSaved(mintReceipt(r.result?.mint))
                    }),
                "data-testid": Testids.cmDuty.ruleDelete,
                children: "Delete rule"
            }) : problem ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: problem
            }) : undefined
        },
        children: [
            /*#__PURE__*/ _jsx(RuleForm, {
                programId: programId,
                value: draft,
                onChange: setDraft
            }),
            error ? /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "caption",
                tone: "danger",
                role: "alert",
                className: "mt-3",
                children: apiErrorMessage(error, 'Could not save the rule')
            }) : null
        ]
    });
}
/**
 * D107: an option period's "N days ARO" rule has no order date until one is
 * logged. When one applied modification exercised the period, its date is
 * offered — never applied until the reviewer clicks (D98).
 */ export function AroSuggestion({ programId, check, canWrite }) {
    const log = useLogContractEvent(programId, {
        success: (r)=>`Order date logged: ${cmDate(r.event?.occurredOn)}. ${mintReceipt(r.result?.mint)}`
    });
    if (!check.suggestedOn) return null;
    const mod = check.suggestedLabel || 'the modification';
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-2",
        "data-testid": Testids.cmDuty.aroSuggestion,
        children: [
            /*#__PURE__*/ _jsxs(SpecraText, {
                size: "caption",
                tone: "muted",
                children: [
                    mod,
                    " exercised this period on ",
                    cmDate(check.suggestedOn),
                    "."
                ]
            }),
            canWrite ? /*#__PURE__*/ _jsxs(SpecraButton, {
                size: "sm",
                variant: "secondary",
                isLoading: log.isPending,
                onClick: ()=>log.mutate({
                        kind: 'receipt_of_order',
                        occurredOn: check.suggestedOn,
                        label: `Reviewer accepted ${mod}'s date`,
                        sourceDocumentId: check.suggestedDocumentId
                    }),
                "data-testid": Testids.cmDuty.aroAccept,
                children: [
                    "Use ",
                    mod,
                    "’s date (",
                    cmDate(check.suggestedOn),
                    ") as the order date"
                ]
            }) : null
        ]
    });
}
